import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TeamActivityTimeline } from "@/components/TeamActivityTimeline";
import { WeeklyDigestCard } from "@/components/WeeklyDigestCard";
import { CircleNotch, Plus, Sparkle, Trash, EnvelopeSimple, UsersThree, CheckCircle } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  ["accountant", "Accountant / CPA"],
  ["partner", "Partner / Co-owner"],
  ["employee", "Employee"],
];

const fmtDate = (value) => value ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export function BusinessSettingsPanel({ business, onBusinessChange }) {
  const [profile, setProfile] = useState({ name: "", industry: "", entity_type: "", state: "", county: "" });
  const [team, setTeam] = useState({ members: [], pending_invites: [], rowan_intro: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "accountant" });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    setProfile({
      name: business?.name || "",
      industry: business?.industry || "",
      entity_type: business?.entity_type || "",
      state: business?.state || "",
      county: business?.county || "",
    });
  }, [business]);

  const loadTeam = async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${business.id}/team-members`);
      setTeam(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load team members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTeam(); /* eslint-disable-next-line */ }, [business?.id]);

  const activeMembers = useMemo(() => team.members || [], [team]);
  const pendingInvites = useMemo(() => team.pending_invites || [], [team]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch(`/businesses/${business.id}`, profile);
      onBusinessChange?.(data);
      toast.success("Business settings updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save business settings.");
    } finally {
      setSavingProfile(false);
    }
  };

  const invite = async () => {
    if (!inviteForm.email.trim()) return toast.error("Add an email first.");
    setInviting(true);
    try {
      const { data } = await api.post(`/businesses/${business.id}/team-members/invite`, inviteForm);
      toast.success("Invite sent.", { description: data.rowan_message });
      setInviteForm({ email: "", role: "accountant" });
      await loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send that invite.");
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (memberId, role) => {
    setBusyId(`role-${memberId}`);
    try {
      await api.patch(`/business-members/${memberId}`, { role });
      toast.success("Role updated.");
      await loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update that role.");
    } finally {
      setBusyId("");
    }
  };

  const resend = async (memberId) => {
    setBusyId(`resend-${memberId}`);
    try {
      const { data } = await api.post(`/business-members/${memberId}/resend`);
      toast.success("Invite resent.", { description: data.rowan_message });
      await loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend that invite.");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (member) => {
    setBusyId(`remove-${member.id}`);
    try {
      const { data } = await api.delete(`/business-members/${member.id}`);
      toast.success("Access removed.", { description: data.rowan_message });
      await loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove access.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="business-settings-panel">
      <div className="bk-rowan-card p-6" data-testid="business-settings-hero">
        <div className="flex items-start gap-3">
          <Sparkle size={18} weight="fill" color="var(--bk-gold)" className="mt-0.5" />
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Business Settings</p>
            <h3 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Bring the whole crew in — without seat fees.</h3>
            <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="business-settings-rowan-intro">Invite as many as you need. Accountants, partners, employees — all free.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
        <section className="bk-card p-6" data-testid="business-settings-profile-card">
          <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Business profile</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <input value={profile.name} onChange={(e) => setProfile((s) => ({ ...s, name: e.target.value }))} data-testid="business-settings-name" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Business name" />
            <input value={profile.industry} onChange={(e) => setProfile((s) => ({ ...s, industry: e.target.value }))} data-testid="business-settings-industry" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Industry" />
            <input value={profile.entity_type} onChange={(e) => setProfile((s) => ({ ...s, entity_type: e.target.value }))} data-testid="business-settings-entity-type" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Entity type" />
            <input value={profile.state} onChange={(e) => setProfile((s) => ({ ...s, state: e.target.value }))} data-testid="business-settings-state" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="State" />
            <input value={profile.county} onChange={(e) => setProfile((s) => ({ ...s, county: e.target.value }))} data-testid="business-settings-county" className="rounded-md border px-3 py-2 text-sm sm:col-span-2" style={{ borderColor: "var(--bk-border)" }} placeholder="County / region" />
          </div>
          <button onClick={saveProfile} disabled={savingProfile} data-testid="business-settings-save-profile" className="bk-btn-primary mt-4 px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {savingProfile ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : <><CheckCircle size={14} weight="fill" /> Save business settings</>}
          </button>
        </section>

        <section className="bk-card p-6" data-testid="business-settings-team-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <UsersThree size={18} weight="duotone" color="var(--bk-accent)" />
                <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Team Members</h4>
              </div>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="business-settings-team-intro">{team.rowan_intro || "These are the people who can help run this business inside IronRoot."}</p>
            </div>
          </div>

          <div className="rounded-[22px] border p-4 mt-5" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="business-settings-invite-form">
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_0.8fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Email</label>
                <input value={inviteForm.email} onChange={(e) => setInviteForm((s) => ({ ...s, email: e.target.value }))} data-testid="business-settings-invite-email" className="rounded-md border px-3 py-2 text-sm mt-1.5 w-full" style={{ borderColor: "var(--bk-border)" }} placeholder="teammate@company.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Role</label>
                <select value={inviteForm.role} onChange={(e) => setInviteForm((s) => ({ ...s, role: e.target.value }))} data-testid="business-settings-invite-role" className="rounded-md border px-3 py-2 text-sm mt-1.5 w-full bg-white" style={{ borderColor: "var(--bk-border)" }}>
                  {ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <button onClick={invite} disabled={inviting} data-testid="business-settings-invite-submit" className="bk-btn-primary px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {inviting ? <><CircleNotch size={14} className="animate-spin" /> Sending…</> : <><Plus size={14} weight="bold" /> Invite someone</>}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="py-12 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="business-settings-team-loading">
              <CircleNotch size={18} className="animate-spin" /> Loading team…
            </div>
          ) : (
            <div className="space-y-6 mt-6">
              <div data-testid="business-settings-team-members-list">
                <h5 className="text-xs uppercase tracking-[0.15em] font-semibold mb-3" style={{ color: "var(--bk-text-2)" }}>Current team</h5>
                <div className="space-y-3">
                  {activeMembers.map((member) => (
                    <div key={member.id} className="rounded-[20px] border p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`business-team-member-${member.id}`}>
                      <div>
                        <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{member.name}</p>
                        <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{member.email} · Last active {fmtDate(member.last_active_at)}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {member.can_edit ? (
                          <select value={member.role} onChange={(e) => changeRole(member.id, e.target.value)} disabled={busyId === `role-${member.id}`} data-testid={`business-team-role-${member.id}`} className="rounded-full border px-3 py-2 text-sm bg-white disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                            {ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        ) : (
                          <span className="px-3 py-2 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`business-team-owner-pill-${member.id}`}>{member.role_label}</span>
                        )}
                        {member.can_remove && (
                          <button onClick={() => remove(member)} disabled={busyId === `remove-${member.id}`} data-testid={`business-team-remove-${member.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: "rgba(178,59,31,0.2)", color: "#B23B1F" }}>
                            {busyId === `remove-${member.id}` ? <CircleNotch size={12} className="animate-spin" /> : <Trash size={12} weight="bold" />} Remove access
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div data-testid="business-settings-pending-invites-list">
                <h5 className="text-xs uppercase tracking-[0.15em] font-semibold mb-3" style={{ color: "var(--bk-text-2)" }}>Pending invites</h5>
                {pendingInvites.length === 0 ? (
                  <div className="rounded-[20px] border p-4 text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="business-settings-pending-empty">No pending invites right now.</div>
                ) : (
                  <div className="space-y-3">
                    {pendingInvites.map((member) => (
                      <div key={member.id} className="rounded-[20px] border p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`business-team-pending-${member.id}`}>
                        <div>
                          <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{member.email}</p>
                          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{member.role_label} · Invited {fmtDate(member.invited_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => resend(member.id)} disabled={busyId === `resend-${member.id}`} data-testid={`business-team-resend-${member.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                            {busyId === `resend-${member.id}` ? <CircleNotch size={12} className="animate-spin" /> : <EnvelopeSimple size={12} weight="bold" />} Resend invite
                          </button>
                          <button onClick={() => remove(member)} disabled={busyId === `remove-${member.id}`} data-testid={`business-team-remove-pending-${member.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: "rgba(178,59,31,0.2)", color: "#B23B1F" }}>
                            {busyId === `remove-${member.id}` ? <CircleNotch size={12} className="animate-spin" /> : <Trash size={12} weight="bold" />} Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      <WeeklyDigestCard />

      {business?.id && <TeamActivityTimeline businessId={business.id} />}
    </div>
  );
}